import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { API } from "@/config";

export const CustomerPaymentPortal = () => {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    cardholder_name: "",
    card_number: "",
    expiration_date: "",
    cvv: "",
    billing_zip: "",
    id_number: "",
    signature_name: "",
    agree: false
  });

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!invoiceNumber.trim()) { toast.error("Please enter your invoice number"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer-portal/lookup`, { invoice_number: invoiceNumber });
      setInvoiceData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invoice not found. Please check your invoice number.");
      setInvoiceData(null);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cardholder_name || !formData.card_number || !formData.expiration_date || !formData.cvv || !formData.billing_zip) {
      toast.error("Please fill in all credit card fields"); return;
    }
    if (!formData.signature_name.trim()) { toast.error("Please type your full legal name to sign"); return; }
    if (!formData.agree) { toast.error("Please agree to the recurring payment authorization"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/customer-portal/authorize`, {
        invoice_number: invoiceData.invoice_number,
        cardholder_name: formData.cardholder_name,
        card_number: formData.card_number,
        expiration_date: formData.expiration_date,
        cvv: formData.cvv,
        billing_zip: formData.billing_zip,
        id_number: formData.id_number,
        signature_name: formData.signature_name,
        signature_date: new Date().toISOString()
      });
      setSubmitted(true);
      toast.success("Authorization submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error submitting authorization");
    }
    setLoading(false);
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <Card className="w-full max-w-lg" data-testid="auth-success-card">
          <CardContent className="p-10 text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Authorization Submitted</h2>
            <p className="text-gray-600">Thank you, your recurring payment authorization for invoice <strong>{invoiceData.invoice_number}</strong> has been received and recorded.</p>
            <p className="text-sm text-gray-500">Your card will be charged <strong>${invoiceData.monthly_payment.toFixed(2)}</strong> monthly on the 15th of each month until the remaining balance is paid.</p>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-400">Ellantes LLC - 1309 Coffeen Avenue, Sheridan, WY 82801</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already signed screen
  if (invoiceData && invoiceData.has_portal_signature) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <Card className="w-full max-w-lg" data-testid="already-signed-card">
          <CardContent className="p-10 text-center space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Already Authorized</h2>
            <p className="text-gray-600">A payment authorization for invoice <strong>{invoiceData.invoice_number}</strong> has already been submitted.</p>
            <p className="text-sm text-gray-500">Signed on: {new Date(invoiceData.portal_signature_date).toLocaleDateString()}</p>
            <p className="text-sm text-gray-500">If you need to update your information, please contact Ellantes LLC directly.</p>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-400">Ellantes LLC - 1309 Coffeen Avenue, Sheridan, WY 82801</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Paid in full screen
  if (invoiceData && invoiceData.balance_due <= 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <Card className="w-full max-w-lg" data-testid="paid-in-full-card">
          <CardContent className="p-10 text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Paid in Full</h2>
            <p className="text-gray-600">Invoice <strong>{invoiceData.invoice_number}</strong> has been paid in full. No further payment authorization is required.</p>
            <p className="text-sm text-gray-500">Thank you for your business!</p>
            <Button variant="outline" onClick={() => { setInvoiceData(null); setInvoiceNumber(""); }} data-testid="lookup-another-btn">Look Up Another Invoice</Button>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-400">Ellantes LLC - 1309 Coffeen Avenue, Sheridan, WY 82801</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <img src={`${API}/logo`} alt="Ellantes" className="h-16 mx-auto" onError={(e) => e.target.style.display='none'} />
          <h1 className="text-3xl font-bold text-gray-900" data-testid="portal-title">Ellantes Payment Authorization</h1>
          <p className="text-gray-500">Securely authorize recurring payments for your account</p>
        </div>

        {/* Invoice Lookup */}
        {!invoiceData && (
          <Card data-testid="invoice-lookup-card">
            <CardHeader>
              <CardTitle>Access Your Account</CardTitle>
              <CardDescription>Enter your invoice number to get started</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <Label>Invoice Number</Label>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())}
                    placeholder="e.g. INV-1001"
                    className="text-lg font-mono"
                    data-testid="invoice-number-input"
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="lookup-btn">
                  {loading ? "Looking up..." : "Continue"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Invoice Details + Authorization Form */}
        {invoiceData && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Summary */}
            <Card data-testid="account-summary-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Account Summary</CardTitle>
                    <CardDescription>{invoiceData.invoice_number}</CardDescription>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setInvoiceData(null); setInvoiceNumber(""); }} data-testid="change-invoice-btn">Change</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">Name</p><p className="font-semibold" data-testid="customer-name-display">{invoiceData.customer_name}</p></div>
                  <div><p className="text-sm text-gray-500">Email</p><p className="font-semibold">{invoiceData.customer_email}</p></div>
                </div>

                {/* Items */}
                <Table>
                  <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {invoiceData.items.map((item, i) => (
                      <TableRow key={i}><TableCell>{item.product_name}</TableCell><TableCell>{item.quantity}</TableCell><TableCell className="text-right">${item.total.toFixed(2)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Financial Summary */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between"><span className="text-gray-600">Total Invoice:</span><span className="font-semibold">${invoiceData.total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-green-600"><span>Payments Made:</span><span className="font-semibold">${invoiceData.total_paid.toFixed(2)}</span></div>
                  <div className="flex justify-between text-red-600 text-lg border-t pt-2"><span className="font-bold">Remaining Balance:</span><span className="font-bold" data-testid="balance-display">${invoiceData.balance_due.toFixed(2)}</span></div>
                </div>

                {invoiceData.monthly_payment > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-blue-700 mb-1">{invoiceData.plan_type === 'lease_to_own' ? 'Lease-to-Own' : 'Financing'} Monthly Payment</p>
                    <p className="text-3xl font-bold text-blue-800" data-testid="monthly-amount-display">${invoiceData.monthly_payment.toFixed(2)}<span className="text-sm font-normal text-blue-600">/month</span></p>
                    {invoiceData.remaining_payments > 0 && <p className="text-sm text-blue-600 mt-1">{invoiceData.remaining_payments} payment{invoiceData.remaining_payments !== 1 ? 's' : ''} remaining - due on the 15th of each month</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credit Card Information */}
            <Card data-testid="cc-form-card">
              <CardHeader>
                <CardTitle>Credit Card Information</CardTitle>
                <CardDescription>Your card will be charged monthly on the 15th until the balance is paid</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Cardholder Name *</Label><Input value={formData.cardholder_name} onChange={(e) => setFormData({...formData, cardholder_name: e.target.value})} placeholder="Name as shown on card" data-testid="cc-name-input" required /></div>
                  <div className="col-span-2"><Label>Card Number *</Label><Input value={formData.card_number} onChange={(e) => setFormData({...formData, card_number: e.target.value})} placeholder="1234 5678 9012 3456" maxLength="19" data-testid="cc-number-input" required /></div>
                  <div><Label>Expiration (MM/YY) *</Label><Input value={formData.expiration_date} onChange={(e) => setFormData({...formData, expiration_date: e.target.value})} placeholder="MM/YY" maxLength="5" data-testid="cc-expiry-input" required /></div>
                  <div><Label>CVV *</Label><Input value={formData.cvv} onChange={(e) => setFormData({...formData, cvv: e.target.value})} placeholder="123" maxLength="4" type="password" data-testid="cc-cvv-input" required /></div>
                  <div><Label>Billing ZIP Code *</Label><Input value={formData.billing_zip} onChange={(e) => setFormData({...formData, billing_zip: e.target.value})} placeholder="90210" data-testid="cc-zip-input" required /></div>
                  <div><Label>ID Number (License/Passport)</Label><Input value={formData.id_number} onChange={(e) => setFormData({...formData, id_number: e.target.value})} placeholder="DL123456" data-testid="cc-id-input" /></div>
                </div>
              </CardContent>
            </Card>

            {/* E-Signature & Agreement */}
            <Card className="border-2 border-gray-800" data-testid="signature-card">
              <CardHeader>
                <CardTitle>Recurring Payment Authorization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gray-50 p-5 rounded-lg text-sm leading-relaxed space-y-3 max-h-64 overflow-y-auto border" data-testid="agreement-text">
                  <p className="font-bold text-base">RECURRING PAYMENT AUTHORIZATION AGREEMENT</p>
                  <p>I, the undersigned, hereby authorize <strong>Ellantes LLC</strong> ("Company") to charge my credit card listed above for the recurring monthly payment amount of <strong>${invoiceData.monthly_payment > 0 ? invoiceData.monthly_payment.toFixed(2) : invoiceData.balance_due.toFixed(2)}</strong> in connection with Invoice <strong>{invoiceData.invoice_number}</strong>.</p>
                  <p><strong>Payment Schedule:</strong> Monthly payments will be processed on or around the <strong>15th of each month</strong> until the remaining balance of <strong>${invoiceData.balance_due.toFixed(2)}</strong> is paid in full{invoiceData.remaining_payments > 0 ? ` (approximately ${invoiceData.remaining_payments} payment${invoiceData.remaining_payments !== 1 ? 's' : ''})` : ''}.</p>
                  <p><strong>Authorization:</strong> This authorization shall remain in full force and effect until the balance is paid in full, or until I provide written cancellation to Ellantes LLC with at least 30 days notice before the next scheduled payment date.</p>
                  <p><strong>Cancellation:</strong> To cancel this authorization, I must notify Ellantes LLC in writing at least 30 days before the next payment date. Cancellation of recurring payments does not cancel my obligation to pay the remaining balance under the original agreement.</p>
                  <p><strong>Failed Payments:</strong> If a scheduled payment is declined, Ellantes LLC may attempt to process the payment again. A returned payment fee may apply as allowed by law.</p>
                  <p><strong>Card Updates:</strong> I agree to notify Ellantes LLC promptly if my card number, expiration date, or billing address changes.</p>
                  <p><strong>Acknowledgment:</strong> I certify that I am an authorized user of this credit card and that I will not dispute the scheduled payments with my credit card company, provided the transactions correspond to the terms indicated in this authorization.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-bold">Electronic Signature *</Label>
                    <p className="text-sm text-gray-500 mb-2">Type your full legal name below to sign this authorization</p>
                    <Input
                      value={formData.signature_name}
                      onChange={(e) => setFormData({...formData, signature_name: e.target.value})}
                      placeholder="Your Full Legal Name"
                      className="text-lg font-serif italic border-b-2 border-gray-800"
                      data-testid="signature-input"
                      required
                    />
                  </div>
                  <div className="text-sm text-gray-500">Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>

                  <label className="flex items-start space-x-3 bg-yellow-50 p-4 rounded-lg border border-yellow-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agree}
                      onChange={(e) => setFormData({...formData, agree: e.target.checked})}
                      className="mt-1 h-5 w-5"
                      data-testid="agree-checkbox"
                    />
                    <span className="text-sm">
                      I have read and agree to the <strong>Recurring Payment Authorization Agreement</strong> above. I authorize Ellantes LLC to charge my credit card <strong>${invoiceData.monthly_payment > 0 ? invoiceData.monthly_payment.toFixed(2) : invoiceData.balance_due.toFixed(2)} monthly</strong> on the 15th of each month until my balance is paid in full.
                    </span>
                  </label>
                </div>

                <Button type="submit" className="w-full h-12 text-lg bg-green-600 hover:bg-green-700" disabled={loading || !formData.agree} data-testid="submit-auth-btn">
                  {loading ? "Submitting..." : "Sign & Authorize Payments"}
                </Button>

                <p className="text-xs text-center text-gray-400">
                  Your information is submitted securely. By clicking above, you agree to the terms of the recurring payment authorization.
                </p>
              </CardContent>
            </Card>

            {/* Footer */}
            <div className="text-center text-xs text-gray-400 pb-8">
              <p>Ellantes LLC - 1309 Coffeen Avenue, Sheridan, WY 82801</p>
              <p>EIN: 33-3145594</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
